// @flow
import type { Usage } from 'digicel-kaizen-library';
import theme from '../../theme-config';
import { isLineOFFSelector } from '../Dashboard';
import type { StateType } from '../reducers';

export const usageStateSelector = (state: StateType): Object[] => state.usage.summarizedUsages?.data || [];

export const usagesSelector = (state: StateType): Object[] => {
  const isLineOFF = isLineOFFSelector(state);
  const usages = usageStateSelector(state).filter(usage => {
    const label = usage.label;
    const isIDD = theme.doughnutConfig.iddDoughnut.includes(label);
    const isRoaming = theme.doughnutConfig.travelDoughnut.includes(label);
    if ((!isIDD && isLineOFF) || isRoaming) return false;

    const isUsedEntirely = !usage.currentValue;
    return !(isIDD && isUsedEntirely);
  });

  if (isLineOFF) {
    const grayedBaseUsages = [{ label: 'DATA_CREDIT' }, { label: 'VOICE_CREDIT' }, { label: 'SMS_CREDIT' }];
    return grayedBaseUsages.concat(usages);
  }

  return usages;
};

export const premiumUsagesSelector = (state: StateType): Object => {
  const premiumUsages = usageStateSelector(state).find(usage => usage.label === 'EXTRA_CREDIT');
  return premiumUsages;
};

export const premiumUsagesCurrentAmountSelector = (state: StateType): ?number => {
  const premiumUsages = premiumUsagesSelector(state);

  return premiumUsages?.currentValue;
};

export const roamingPackageUsagesSelector = (state: StateType): ?(PackageUsages[]) => {
  const usageState = usageStateSelector(state);
  if (!usageState) return [];

  const roamingPackageUsages = {};
  usageState.forEach(usage => {
    const label = usage.label.replace('_CREDIT', '');
    const isRLH = label.includes('RLH');
    const isRoaming = label.includes('ROAMING') || isRLH;

    if (isRoaming) {
      const zone = label.includes('RLH2')
        ? 'EXTENDED_ROAM_LIKE_HOME'
        : isRLH
          ? 'ROAM_LIKE_HOME'
          : label.substr(label.lastIndexOf('G') + 1);

      if (!roamingPackageUsages[zone]) {
        roamingPackageUsages[zone] = {};
      }

      if (!roamingPackageUsages[zone].endDateTime) {
        roamingPackageUsages[zone].endDateTime = usage.endDateTime;
      }

      const status = usage.status;
      if (!roamingPackageUsages[zone].status || status === 'ROAMING') {
        roamingPackageUsages[zone].status = status;
      }

      if (!roamingPackageUsages[zone].zone) {
        roamingPackageUsages[zone].zone = zone;
      }

      if (!roamingPackageUsages[zone].usages) {
        roamingPackageUsages[zone].usages = [];
      }

      const totalRoamingPacks = usage.totalRoamingPacks;
      if (!roamingPackageUsages[zone].totalRoamingPacks && totalRoamingPacks != null) {
        roamingPackageUsages[zone].totalRoamingPacks = totalRoamingPacks;
      }

      roamingPackageUsages[zone].usages.push(usage);
    }
  });

  // $FlowFixMe object.values
  return Object.values(roamingPackageUsages).sort((usageA: PackageUsages, usageB: PackageUsages) => {
    if (!usageA.endDateTime || !usageB.endDateTime) return 0;
    return usageA.endDateTime - usageB.endDateTime;
  });
};

export const travelUsagesSelector = (state: StateType): ?(PackageUsages[]) => {
  const usageState = usageStateSelector(state);
  if (!usageState) return [];

  const travelUsages = {};
  usageState.forEach(usage => {
    const label = usage.label.replace('_CREDIT', '');
    const isRoaming = label.includes('ROA') && label.includes('RDM');

    if (isRoaming) {
      let travelZone: string = '';
      if (label.includes('RDM1')) {
        travelZone = 'TRAVEL_ZONE_1';
      } else if (label.includes('RDM2')) {
        travelZone = 'TRAVEL_ZONE_2';
      }

      if (!travelUsages[travelZone]) {
        travelUsages[travelZone] = {};
      }

      if (!travelUsages[travelZone].endDateTime) {
        travelUsages[travelZone].endDateTime = usage.endDateTime;
      }

      const status = usage.status;
      if (!travelUsages[travelZone].status || status === 'ROAMING') {
        travelUsages[travelZone].status = status;
      }

      if (!travelUsages[travelZone].zone) {
        travelUsages[travelZone].zone = travelZone;
      }

      if (!travelUsages[travelZone].usages) {
        travelUsages[travelZone].usages = [];
      }

      travelUsages[travelZone].usages.push(usage);
    }
  });

  // $FlowFixMe object.values
  return Object.values(travelUsages).sort((usageA: PackageUsages, usageB: PackageUsages) => {
    if (!usageA.endDateTime || !usageB.endDateTime) return 0;
    return usageA.endDateTime - usageB.endDateTime;
  });
};

export const isNetworkFailureSelector = (state: StateType) => {
  const summarizedUsages = state.usage.summarizedUsages;
  return summarizedUsages.failure && summarizedUsages.networkFailure;
};

export type PackageUsages = {
  endDateTime: number,
  status: 'PENDING_ROAMING' | 'ROAMING',
  zone: string,
  usages: Usage[],
  totalRoamingPacks?: number,
};
